import { Box, styled } from '@mui/material';

export type ValueBadgeVariant = 'negative' | 'zero' | 'positive' | 'neutral';

export const ValueBadge = styled(Box)<{ variant: ValueBadgeVariant }>(({
  theme,
  variant
}) => {
  /**
   * Every variant is a solid theme-palette pairing — a light `.container` fill under a darker `.dark`
   * line and label — the same convention the afsprakenplanning (MRP) calendar colours its appointments
   * with, so the two pages read from one place. red → error, blue → info. Green and yellow have no
   * semantic-palette equivalent, so they live in the theme as `custom.constants.rosterBadge` — a
   * calmer green than the theme's vivid `success`, and a yellow rather than the orange the theme's
   * `warning` is — and are read from there rather than from `@mui/material/colors` directly.
   *
   * Solid container fills rather than a 12% alpha wash: the wash flattens every hue towards white,
   * leaving a shortage and a surplus only a few dE apart and unseparable for protan/deutan viewers.
   * Red and green stay the classic colour-blind confusion pair, but they never carry the same content
   * — a shortage shows a "-N", a met week a checkmark — so the number versus the tick separates them
   * regardless of hue.
   */
  const { met, surplus } = theme.custom.constants.rosterBadge;
  const variants = {
    // Shortage.
    negative: {
      line: theme.palette.error.dark,
      fill: theme.palette.error.container
    },
    // Met: on track, drawn as a checkmark. A calmer green than the theme's vivid success.
    zero: {
      line: met.line,
      fill: met.fill
    },
    // Surplus. Yellow rather than the orange the theme's warning is.
    positive: {
      line: surplus.line,
      fill: surplus.fill
    },
    // Roll-up / summary — the neutral blue.
    neutral: {
      line: theme.palette.info.dark,
      fill: theme.palette.info.container
    }
  };
  const { line, fill } = variants[variant];

  return {
    height: '100%',
    width: '100%',
    marginBlock: theme.spacing(1),
    // A tight 4px corner rather than theme.shape.borderRadius, which rounds the small badge too much.
    borderRadius: 4,
    border: '1px solid',
    borderColor: line,
    backgroundColor: fill,
    color: line,
    fontSize: theme.typography.caption.fontSize,
    lineHeight: 1.5,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(0.5)
  };
});
